import { useParams, Link } from 'react-router-dom';
import { SeoHead, organizationLd, webSiteLd } from '../seo';
import BangaloreMonthly from './guides/BangaloreMonthly';
import DelhiMetroParking from './guides/DelhiMetroParking';
import EVChargingGuide from './guides/EVChargingGuide';
import NegotiateParking from './guides/NegotiateParking';
import TwoWheelerSafety from './guides/TwoWheelerSafety';
import AirportParking from './guides/AirportParking';
import ApartmentVisitor from './guides/ApartmentVisitor';
import CommercialOperator from './guides/CommercialOperator';
import CommercialVsAggregator from './guides/CommercialVsAggregator';
import SeekerStrategy from './guides/SeekerStrategy';

interface GuideMeta {
  component: () => React.JSX.Element;
  title: string;
  description: string;
  ogImage: string;
  publishedTime: string;
  readMinutes: number;
  category: string;
}

const GUIDES: Record<string, GuideMeta> = {
  'how-to-find-monthly-parking-in-bangalore': {
    component: BangaloreMonthly,
    title: 'How to Find Monthly Parking in Bangalore Without Paying Brokerage',
    description:
      "A step-by-step Bangalore-specific guide to finding monthly car, bike, and scooter parking. Includes real price data, neighbourhood-by-neighbourhood tips, and how to use Parksy to bypass the broker.",
    ogImage: 'https://parksy.rent/og/guides/bangalore-monthly.png',
    publishedTime: '2026-06-22',
    readMinutes: 9,
    category: 'cities',
  },
  'parking-near-delhi-metro-stations': {
    component: DelhiMetroParking,
    title: 'Parking Near Delhi Metro Stations: A Complete Park-and-Ride Guide',
    description:
      "Every Delhi Metro station with reliable two-wheeler and car parking, organised by line. How to find a covered, CCTV-monitored spot within walking distance — without paying the metro operator's full day rate.",
    ogImage: 'https://parksy.rent/og/guides/delhi-metro.png',
    publishedTime: '2026-06-22',
    readMinutes: 11,
    category: 'commute',
  },
  'ev-charging-parking-guide-india': {
    component: EVChargingGuide,
    title: "The EV Owner's Parking Guide: How to Find a Spot That Actually Charges",
    description:
      'How to find an EV charging parking spot in India that is not ICE-d, broken, or 2 km from your route. Filter strategy, host selection, and connector compatibility — explained.',
    ogImage: 'https://parksy.rent/og/guides/ev-charging.png',
    publishedTime: '2026-06-22',
    readMinutes: 8,
    category: 'ev',
  },
  'negotiate-parking-rent-india': {
    component: NegotiateParking,
    title: 'How to Negotiate Parking Rent in India (and What to Say)',
    description:
      'Scripts, tactics, and the data to back you up when negotiating monthly parking rent. Includes what to say to RWAs, commercial operators, and individual driveway owners.',
    ogImage: 'https://parksy.rent/og/guides/negotiate-parking.png',
    publishedTime: '2026-06-22',
    readMinutes: 7,
    category: 'parking-tips',
  },
  'safe-parking-tips-two-wheelers': {
    component: TwoWheelerSafety,
    title: 'Two-Wheeler Parking Safety in India: A Practical Guide',
    description:
      'Anti-theft tips, what to look for in a covered two-wheeler spot, and how to reduce your bike\'s risk of being towed or stolen in Indian metros.',
    ogImage: 'https://parksy.rent/og/guides/two-wheeler-safety.png',
    publishedTime: '2026-06-22',
    readMinutes: 6,
    category: 'two-wheeler',
  },
  'airport-parking-mumbai-bangalore-delhi': {
    component: AirportParking,
    title: 'Airport Parking in Mumbai, Bangalore, and Delhi: A City-by-City Guide',
    description:
      "Long-stay airport parking rates, alternatives near each major Indian airport, and how to book a covered, 24/7 spot within 3 km of the terminal for 50–70% less than the airport's own lot.",
    ogImage: 'https://parksy.rent/og/guides/airport-parking.png',
    publishedTime: '2026-06-22',
    readMinutes: 10,
    category: 'airport',
  },
  'apartment-visitor-parking-solutions': {
    component: ApartmentVisitor,
    title: 'How RWAs and Residents Can Solve the Visitor Parking Problem',
    description:
      'Why apartment visitor parking is the #1 source of resident-vs-RWA fights, and 4 practical solutions that work — including listing your unused slot on Parksy.',
    ogImage: 'https://parksy.rent/og/guides/apartment-visitor.png',
    publishedTime: '2026-06-22',
    readMinutes: 7,
    category: 'parking-tips',
  },
  'commercial-parking-business-guide-india': {
    component: CommercialOperator,
    title: 'Starting a Commercial Parking Lot in India: A Practical Guide',
    description:
      'How to start, license, and operate a paid commercial parking lot in an Indian metro. Capex, opex, regulatory requirements, and how to market the spot using free tools like Parksy.',
    ogImage: 'https://parksy.rent/og/guides/commercial-operator.png',
    publishedTime: '2026-06-22',
    readMinutes: 12,
    category: 'operator',
  },
  'commercial-vs-aggregator': {
    component: CommercialVsAggregator,
    title: 'Commercial Operators vs Aggregator Platforms: A Side-by-Side Look',
    description:
      "Aggregator platforms (Park+, NoBroker Parking, JustPark) take 15-25% commission on every booking. Listing on Parksy is free. A factual feature-by-feature comparison of the three models, as of 2024.",
    ogImage: 'https://parksy.rent/og/guides/commercial-vs-aggregator.png',
    publishedTime: '2026-06-22',
    readMinutes: 6,
    category: 'operator',
  },
  'seeker-strategy-india': {
    component: SeekerStrategy,
    title: "A Seeker's Complete Strategy for Finding Parking in India",
    description:
      'The end-to-end seeker playbook: from dropping your first pin to locking in a long-term spot. Covers every vehicle type, every city, and every price tier.',
    ogImage: 'https://parksy.rent/og/guides/seeker-strategy.png',
    publishedTime: '2026-06-22',
    readMinutes: 8,
    category: 'parking-tips',
  },
};

export const GUIDE_SLUGS = Object.keys(GUIDES);
export const GUIDE_META = GUIDES;

export default function Guide() {
  const { slug = '' } = useParams<{ slug: string }>();
  const meta = GUIDES[slug];

  if (!meta) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12">
        <SeoHead
          title="Guide not found — Parksy"
          description="The guide you're looking for doesn't exist."
          canonical={`https://parksy.rent/guides/${slug}`}
          noindex
          schemas={[organizationLd(), webSiteLd()]}
        />
        <h1 className="text-2xl font-bold text-gray-900">Guide not found</h1>
        <p className="text-gray-600 mt-2">No guide matches "{slug}".</p>
        <Link to="/guides" className="text-parksy-600 hover:underline mt-4 inline-block">
          Back to all guides →
        </Link>
      </div>
    );
  }

  const Component = meta.component;
  return <Component />;
}
